import { Editor, Transforms, Element as SlateElement, Range, Path, Text, BaseEditor } from 'slate';
import { ReactEditor } from 'slate-react';

export interface CustomText {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  fontSize?: number;
  fontFamily?: string;
  strikethrough?: boolean;
  superscript?: boolean;
  subscript?: boolean;
}

export type InlineElement = LinkElement;
export type TextOrInline = CustomText | InlineElement;

export interface ParagraphElement {
  type: 'paragraph';
  align?: 'left' | 'center' | 'right' | 'justify';
  children: TextOrInline[];
}
export interface HeadingOneElement {
  type: 'heading-one';
  align?: 'left' | 'center' | 'right' | 'justify';
  children: TextOrInline[];
}
export interface HeadingTwoElement {
  type: 'heading-two';
  align?: 'left' | 'center' | 'right' | 'justify';
  children: TextOrInline[];
}
export interface HeadingThreeElement {
  type: 'heading-three';
  align?: 'left' | 'center' | 'right' | 'justify';
  children: TextOrInline[];
}
export interface BlockQuoteElement {
  type: 'blockquote';
  align?: 'left' | 'center' | 'right' | 'justify';
  children: TextOrInline[];
}
export interface ListItemElement {
  type: 'list-item';
  children: TextOrInline[];
}

export interface OrderedListElement {
  type: 'ordered-list';
  children: ListItemElement[];
}
export interface UnorderedListElement {
  type: 'unordered-list';
  children: ListItemElement[];
}

export interface LinkElement {
  type: 'link';
  href: string;
  children: CustomText[];
}

export type CustomElement =
  | ParagraphElement
  | HeadingOneElement
  | HeadingTwoElement
  | HeadingThreeElement
  | BlockQuoteElement
  | OrderedListElement
  | UnorderedListElement
  | ListItemElement
  | LinkElement;

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

export type MarkFormat =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'superscript'
  | 'subscript'
  | 'color'
  | 'fontSize'
  | 'fontFamily';

export type BlockFormat =
  | 'paragraph'
  | 'heading-one'
  | 'heading-two'
  | 'heading-three'
  | 'blockquote'
  | 'list-item'
  | 'ordered-list'
  | 'unordered-list';

export type AlignFormat = 'left' | 'center' | 'right' | 'justify';

const editorStickyMarks = new WeakMap<Editor, Record<string, any>>();

const getStickyMarks = (editor: Editor) => {
  if (!editorStickyMarks.has(editor)) editorStickyMarks.set(editor, {});
  return editorStickyMarks.get(editor)!;
};

const applyStickyMarks = (editor: Editor) => {
  const marks = getStickyMarks(editor);
  for (const key in marks) {
    Editor.addMark(editor, key, marks[key]);
  }
};

const clearStickyMarks = (editor: Editor) => {
  editorStickyMarks.set(editor, {});
};

const isMarkActive = (editor: Editor, format: MarkFormat) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true || marks[format] !== undefined : false;
};

const getMarkValue = (editor: Editor, format: MarkFormat) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] : undefined;
};

/**
 * Returns the color from the actual leaf node at cursor, ignoring sticky marks.
 * This ensures the color box shows the real text color, not a stale sticky value.
 */
const getLeafColor = (editor: Editor): string | undefined => {
  const { selection } = editor;
  if (!selection) return undefined;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: selection.focus,
      match: (n) => Text.isText(n),
    }),
  );

  if (match) {
    const [node] = match;
    if (node.color) return node.color;
  }

  // If no explicit color mark, checking if we're inside a link
  const [linkMatch] = Array.from(
    Editor.nodes(editor, {
      at: selection.focus,
      match: (n) => SlateElement.isElement(n) && n.type === 'link',
    }),
  );

  if (linkMatch) {
    return '#2BB9BB';
  }

  return undefined;
};

const clearStickyColor = (editor: Editor) => {
  const marks = getStickyMarks(editor);
  delete marks['color'];
};

const toggleMark = (editor: Editor, format: MarkFormat, value?: any) => {
  const { selection } = editor;
  if (!selection) return;

  const isCollapsed = Range.isCollapsed(selection);
  const isActive = isMarkActive(editor, format);
  const stickyMarks = getStickyMarks(editor);

  if (value !== undefined) {
    Editor.addMark(editor, format, value);
    stickyMarks[format] = value;
    if (isCollapsed) Editor.addMark(editor, format, value);
  } else {
    if (isActive) {
      Editor.removeMark(editor, format);
      delete stickyMarks[format];
    } else {
      Editor.addMark(editor, format, true);
      stickyMarks[format] = true;
    }
  }
};

const toggleSuperscript = (editor: Editor) => {
  const stickyMarks = getStickyMarks(editor);
  const isActive = isMarkActive(editor, 'superscript');
  const hasSubscript = isMarkActive(editor, 'subscript');

  if (hasSubscript) {
    Editor.removeMark(editor, 'subscript');
    delete stickyMarks['subscript'];
  }

  if (isActive) {
    Editor.removeMark(editor, 'superscript');
    delete stickyMarks['superscript'];
  } else {
    Editor.addMark(editor, 'superscript', true);
    stickyMarks['superscript'] = true;
  }
};

const toggleSubscript = (editor: Editor) => {
  const stickyMarks = getStickyMarks(editor);
  const isActive = isMarkActive(editor, 'subscript');
  const hasSuperscript = isMarkActive(editor, 'superscript');

  if (hasSuperscript) {
    Editor.removeMark(editor, 'superscript');
    delete stickyMarks['superscript'];
  }

  if (isActive) {
    Editor.removeMark(editor, 'subscript');
    delete stickyMarks['subscript'];
  } else {
    Editor.addMark(editor, 'subscript', true);
    stickyMarks['subscript'] = true;
  }
};

const LIST_TYPES = ['ordered-list', 'unordered-list'];

const isBlockActive = (editor: Editor, format: BlockFormat) => {
  const [match] = Array.from(
    Editor.nodes(editor, {
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
    }),
  );
  return !!match;
};

const toggleBlock = (editor: Editor, format: BlockFormat) => {
  const isActive = CustomEditor.isBlockActive(editor, format);
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes((n as SlateElement).type),
    split: true,
  });

  if (isList) {
    const newType: SlateElement['type'] = isActive ? 'paragraph' : 'list-item';
    Transforms.setNodes(editor, { type: newType });

    if (!isActive) {
      Transforms.wrapNodes(editor, { type: format, children: [] });
    }
  } else {
    Transforms.setNodes(
      editor,
      { type: isActive ? 'paragraph' : format },
      { match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n) },
    );
  }
};

const isAlignActive = (editor: Editor, format: AlignFormat) => {
  const [match] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && 'align' in n && n.align === format,
  });
  return !!match;
};

const toggleAlign = (editor: Editor, format: AlignFormat) => {
  const newProperties: Partial<SlateElement> = {
    align: isAlignActive(editor, format) ? undefined : format,
  };
  Transforms.setNodes<SlateElement>(editor, newProperties);
};

const createLinkNode = (href: string, text: string) => ({
  type: 'link' as const,
  href,
  children: [{ text }],
});

const isLinkActive = (editor: Editor): boolean => {
  const [match] = Array.from(
    Editor.nodes(editor, {
      match: (n) => SlateElement.isElement(n) && n.type === 'link',
    }),
  );
  return !!match;
};

const insertLink = (editor: Editor, url: string) => {
  if (!url) return;

  const { selection } = editor;
  const link = createLinkNode(url, 'Link');

  if (selection) {
    const [parent, parentPath] = Editor.parent(editor, selection.focus.path);
    if (SlateElement.isElement(parent) && parent.type === 'link') {
      Transforms.unwrapNodes(editor, {
        match: (n) => SlateElement.isElement(n) && n.type === 'link',
      });
    }

    if (SlateElement.isElement(parent) && editor.isVoid(parent)) {
      const paragraph: ParagraphElement = {
        type: 'paragraph',
        children: [link],
      };
      Transforms.insertNodes(editor, paragraph, {
        at: Path.next(parentPath),
        select: true,
      });
    } else if (Range.isCollapsed(selection)) {
      Transforms.insertNodes(editor, link, { select: true });
    } else {
      Transforms.wrapNodes(editor, link, { split: true });
      Editor.removeMark(editor, 'color');
    }
  } else {
    const paragraph: ParagraphElement = {
      type: 'paragraph',
      children: [link],
    };
    Transforms.insertNodes(editor, paragraph);
  }
};

export const CustomEditor = {
  isMarkActive,
  getMarkValue,
  getLeafColor,
  clearStickyColor,
  toggleMark,
  toggleSuperscript,
  toggleSubscript,
  isBlockActive,
  toggleBlock,
  isAlignActive,
  toggleAlign,
  applyStickyMarks,
  clearStickyMarks,
  insertLink,
  isLinkActive,
};
